/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useEffect, useRef } from "react";
import {
  useMarkAllNotificationsRead,
  useNotificationHistory,
} from "@/api/notificationsApi";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { trackEvent } from "@/utils/analytics";
import { faBell, faTriangleExclamation } from "@/utils/iconLibraryTreeShake";
import LoadingCircle from "@/utils/LoadingCircle";
import { NotificationItem } from "./NotificationItem";

interface NotificationDropdownProps {
  onPushEnabled?: () => void;
}

const NotificationEnableBanner: React.FC<NotificationDropdownProps> = ({
  onPushEnabled,
}) => {
  const {
    permission,
    isSubscribed,
    isSupported,
    isEnabled,
    error,
    isLoading,
    requestPermissionAndSubscribe,
  } = usePushNotifications();

  const shownFiredRef = useRef(false);
  const shouldRender = isSupported && isEnabled && !isSubscribed;

  useEffect(() => {
    if (!shouldRender) return;
    if (shownFiredRef.current) return;
    shownFiredRef.current = true;
    trackEvent("notification_banner_shown", { permission });
  }, [shouldRender, permission]);

  // Don't show if push is active, not supported, or not server-enabled
  if (!shouldRender) {
    return null;
  }

  if (permission === "denied") {
    return (
      <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mt-0.5 text-amber-400 shrink-0"
          />
          <div>
            <p className="text-sm font-medium text-slate-200">
              Notifications are blocked
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Update your browser settings to allow notifications from this site.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const title =
    permission === "granted"
      ? "Enable push on this browser"
      : "Enable push notifications";

  return (
    <div className="border-b border-slate-600 bg-slate-800 px-4 py-3">
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={faBell} className="mt-0.5 text-orange-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-200">{title}</p>
          <p className="text-xs text-slate-400 mt-1">
            Get race alerts even when this tab is closed.
          </p>
          {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
          <button
            onClick={async () => {
              trackEvent("notification_banner_cta_clicked", { permission });

              let didOpenSettings = false;
              const openSettings = () => {
                if (didOpenSettings) {
                  return;
                }
                didOpenSettings = true;
                onPushEnabled?.();
              };

              const didEnable = await requestPermissionAndSubscribe({
                onPermissionGranted: openSettings,
              });
              const browserPermissionGranted =
                "Notification" in window && Notification.permission === "granted";
              if (didEnable || browserPermissionGranted) {
                openSettings();
              }
            }}
            disabled={isLoading}
            className="mt-2 rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            {isLoading ? "Enabling..." : "Enable"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onPushEnabled,
}) => {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotificationHistory();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();

  const notifications = data?.pages.flatMap(p => p.results) ?? [];

  return (
    <div className="rounded-lg border border-slate-600 bg-slate-900 shadow-xl">
      {/* Push enable banner (shown when push not active) */}
      <NotificationEnableBanner onPushEnabled={onPushEnabled} />

      {isLoading ? (
        <div className="p-4">
          <div className="flex flex-col items-center justify-center gap-2">
            <LoadingCircle className="h-8 w-8" />
            <p className="text-slate-400 text-sm">Loading notifications...</p>
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="p-4">
          <p className="text-slate-400 text-sm text-center">No notifications yet</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-600">
            <h3 className="font-semibold text-slate-200">Notifications</h3>
            <button
              onClick={() => markAllRead()}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
              type="button"
            >
              Mark all read
            </button>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.map(notification => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
            {hasNextPage && (
              <div className="px-4 py-3 text-center border-t border-slate-700/50">
                <button
                  type="button"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="text-xs text-cPurple-400 hover:text-cPurple-300 transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
